import cookieUtil from 'tradera/utils/cookie';
import {
    REGION_PICKER_DISMISSED,
    SHIPPING_COUNTRY
} from 'tradera/constants/cookies';
import { fetchAvailableShippingCountries } from './api';
import {
    selectIsLoadingAvailableShippingCountries,
    selectHasLoadedAvailableShippingCountries
} from './selectors';

import {
    initialize,
    setShippingCountry,
    setAvailableShippingCountries,
    beginLoadingAvailableShippingOptions,
    setDefaultShippingCountry
} from './reducer';
import { setPreferredCurrencyThunk } from 'tradera/state/multi-currency/actions';
import { setPreferredLanguage } from 'tradera/state/language/actions';
import Analytics from 'packages/analytics';
import {
    getCountryCodeFromLanguage,
    getLanguageFromCountryCode
} from 'tradera/localization/languagecode';
import { selectMemberCountryCodeIso2 } from 'tradera/state/member/selectors';
import { getInitialState } from './initial-state';
import GdprSettings from 'tradera/utils/gdpr-settings';
import { selectPreferredLanguageCode } from 'tradera/state/language/selectors';
import { selectIsFeatureEnabled } from '../environment/selectors';

export const updateShippingRegion = (countryCodeIso2) => (dispatch) => {
    dispatch(setShippingCountry({ countryCodeIso2 }));
};

export const removeShippingCountryCookieIfSwedish =
    (geolocation) => (dispatch) => {
        if (
            geolocation.isoCode === 'SE' &&
            cookieUtil.hasCookie(SHIPPING_COUNTRY)
        ) {
            cookieUtil.eraseCookie(SHIPPING_COUNTRY);
            dispatch(setDefaultShippingCountry());
        }
    };

export const getAvailableShippingCountries =
    () => async (dispatch, getState) => {
        if (
            selectIsLoadingAvailableShippingCountries(getState()) ||
            selectHasLoadedAvailableShippingCountries(getState())
        ) {
            return;
        }

        dispatch(beginLoadingAvailableShippingOptions());
        const { shippingCountries } = await fetchAvailableShippingCountries();
        dispatch(setAvailableShippingCountries({ shippingCountries }));
    };

export const onLocalizationShippingRegionSelected =
    ({ countryCodeIso2, currencyCode, toLocalizedUrl }) =>
    (dispatch, getState) => {
        const languageCodeIso2 = getLanguageFromCountryCode(
            countryCodeIso2,
            selectIsFeatureEnabled('french-dutch-selectable')(getState())
        );

        Analytics.trackEvent({
            category: 'Top navigation',
            action: 'Localization menu tooltip',
            label: `Set localization region - ${countryCodeIso2}`
        });

        dispatch(getAvailableShippingCountries()); //make sure we have loaded countries
        dispatch(updateShippingRegion(countryCodeIso2));
        dispatch(setPreferredCurrencyThunk(currencyCode));
        dispatch(setPreferredLanguage(languageCodeIso2, toLocalizedUrl));
    };

const getFallbackShippingCountry = (state) =>
    getCountryCodeFromLanguage(selectPreferredLanguageCode(state));

export const initializeFromInitData = (initData) => (dispatch, getState) => {
    const memberCountryCodeIso2 = initData.memberCountryCodeIso2;
    const initialState = getInitialState({
        memberCountryCodeIso2,
        isFunctionalCookiesEnabled: GdprSettings.isFunctionalEnabled(),
        hasRegionPickerDismissedCookie: cookieUtil.hasCookie(
            REGION_PICKER_DISMISSED
        ),
        shippingCountryCookieValue: cookieUtil.readCookie(SHIPPING_COUNTRY),
        fallbackShippingCountry: getFallbackShippingCountry(getState())
    });
    dispatch(initialize(initialState));
};

export const initializeFromState =
    (gdprSettings, ssrCookieUtil) => (dispatch, getState) => {
        const memberCountryCodeIso2 = selectMemberCountryCodeIso2(getState());
        const initialState = getInitialState({
            memberCountryCodeIso2,
            isFunctionalCookiesEnabled: gdprSettings.isFunctionalEnabled(),
            hasRegionPickerDismissedCookie: ssrCookieUtil.hasCookie(
                REGION_PICKER_DISMISSED
            ),
            shippingCountryCookieValue:
                ssrCookieUtil.readCookie(SHIPPING_COUNTRY),
            fallbackShippingCountry: getFallbackShippingCountry(getState())
        });
        dispatch(initialize(initialState));
    };
